import { getItem } from "owa-local-storage";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isOcpsPolicyEnabled, isPoliciesEnabledForAccount, isUserAllowedToChangeSettings, OcpsPolicyFeature, UnifiedInboxPolicySettingId, } from "owa-owsprime-policies-store";

/**
 * Gets whether the Unified Inbox ("show all accounts") node should be shown, honoring the
 * OCPS UnifiedInbox admin policy on the global settings account.
 *
 * - Policy is only consulted when policies are enabled for the account
 *   (isPoliciesEnabledForAccount) — the same gate used before fetching/processing the policy
 *   data. When that flight is off, the locally persisted preference is used (defaulting to shown).
 * - If the admin disallows user changes (ShowAllAccountsViewUserOverride = false), the policy
 *   value ShowAllAccountsView is forced, overriding any local-storage preference.
 * - If the admin allows changes, a stored local-storage preference wins; in its absence the
 *   policy value ShowAllAccountsView is used as the default.
 * - When the policy value itself is undefined (or not yet fetched), this falls back to the app
 *   default "shown" via isOcpsPolicyEnabled's enabledIfUndefined.
 */
export function shouldShowAllAccountsNode(): boolean {
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    const stored = getItem(window, "IsUnifiedInboxFolderTreeHidden"); // 'true' | 'false' | null
    
    // If OCPS policies aren't enabled for the account, skip all policy calculations and rely
    // solely on the locally persisted preference (absent value defaults to shown).
    if (!isPoliciesEnabledForAccount(mailboxInfo)) {
        return stored !== "true";
    }
    
    // enabledIfUndefined=true => app default "shown" whenever policy data is absent / not yet loaded
    const policyShow = isOcpsPolicyEnabled(UnifiedInboxPolicySettingId.ShowAllAccountsView, mailboxInfo, true /* enabledIfUndefined */);
    
    // Admin forces the value => ignore local storage entirely
    if (!isUserAllowedToChangeSettings(OcpsPolicyFeature.UnifiedInbox, mailboxInfo)) {
        return policyShow;
    }
    
    // User may change: stored preference wins; otherwise the policy value is the default
    if (stored !== null) {
        return stored !== "true";
    }
    
    return policyShow;
}
